import React, { useMemo, useState } from 'react';
import './ImageGallery.css';
import { formatBytes, formatDateTime, SIZE_VARIANTS } from '../lib/format';

function ImageGallery({ images, onDeleteImage, onBulkDelete, loading }) {
  const [copied, setCopied] = useState(null);
  const [failedPreviews, setFailedPreviews] = useState({});
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('newest');
  const [selected, setSelected] = useState([]);
  const [active, setActive] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const next = images.filter((image) =>
      q ? image.filename.toLowerCase().includes(q) : true
    );
    next.sort((a, b) => {
      if (sort === 'largest') return (b.size || 0) - (a.size || 0);
      if (sort === 'oldest') {
        return new Date(a.uploadedAt) - new Date(b.uploadedAt);
      }
      return new Date(b.uploadedAt) - new Date(a.uploadedAt);
    });
    return next;
  }, [images, query, sort]);

  const getPreviewSrc = (image) => {
    if (failedPreviews[image.filename]) {
      return image.fallbackUrl || image.previewUrl || image.url;
    }
    return image.previewUrl || image.url;
  };

  const copy = async (value, key = value) => {
    await navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 1600);
  };

  const toggleSelect = (filename) => {
    setSelected((prev) =>
      prev.includes(filename) ? prev.filter((name) => name !== filename) : [...prev, filename]
    );
  };

  const toggleAll = () => {
    if (selected.length === filtered.length) setSelected([]);
    else setSelected(filtered.map((image) => image.filename));
  };

  return (
    <div className="image-gallery">
      <div className="gallery-toolbar">
        <div>
          <h3 className="panel-title">Catalog</h3>
          <p className="gallery-count">{filtered.length} of {images.length} assets</p>
        </div>
        <div className="gallery-tools">
          <input
            className="toolbar-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by filename"
          />
          <select className="toolbar-select" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="largest">Largest</option>
          </select>
          {selected.length > 0 && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => onBulkDelete(selected)}
              disabled={loading}
            >
              Delete {selected.length}
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="empty-hint">No images in this view</p>
      ) : (
        <>
          <label className="select-all">
            <input
              type="checkbox"
              checked={selected.length === filtered.length && filtered.length > 0}
              onChange={toggleAll}
            />
            Select all
          </label>
          <div className="gallery-grid">
            {filtered.map((image) => (
              <article
                key={image.filename}
                className={`gallery-card ${active?.filename === image.filename ? 'is-active' : ''}`}
              >
                <div className="gallery-preview">
                  <img
                    src={getPreviewSrc(image)}
                    alt={image.filename}
                    loading="lazy"
                    onError={() =>
                      setFailedPreviews((prev) => ({ ...prev, [image.filename]: true }))
                    }
                    onClick={() => setActive(image)}
                  />
                  <label className="gallery-check">
                    <input
                      type="checkbox"
                      checked={selected.includes(image.filename)}
                      onChange={() => toggleSelect(image.filename)}
                    />
                  </label>
                </div>
                <div className="gallery-info">
                  <p className="gallery-filename" title={image.filename}>{image.filename}</p>
                  <p className="gallery-meta">
                    <span>{formatBytes(image.size)}</span>
                    <span>{formatDateTime(image.uploadedAt)}</span>
                  </p>
                  <div className="variant-row">
                    {SIZE_VARIANTS.map((variant) => (
                      <button
                        key={variant.key}
                        type="button"
                        className="chip"
                        title={variant.hint}
                        onClick={() =>
                          copy(image.variants?.[variant.key] || image.url, `${image.filename}-${variant.key}`)
                        }
                      >
                        {copied === `${image.filename}-${variant.key}` ? 'Copied' : variant.label}
                      </button>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </>
      )}

      {active && (
        <div className="inspector-backdrop" onClick={() => setActive(null)} role="presentation">
          <aside className="inspector" onClick={(e) => e.stopPropagation()}>
            <div className="inspector-preview">
              <img src={getPreviewSrc(active)} alt={active.filename} />
            </div>
            <h4>{active.filename}</h4>
            <p className="gallery-meta">
              <span>{formatBytes(active.size)}</span>
              <span>{formatDateTime(active.uploadedAt)}</span>
            </p>
            <p className="inspector-label">Delivery URLs</p>
            {SIZE_VARIANTS.map((variant) => (
              <div key={variant.key} className="inspector-url">
                <span>{variant.label} · {variant.hint}</span>
                <button
                  type="button"
                  className="btn"
                  onClick={() => copy(active.variants?.[variant.key] || active.url, variant.key)}
                >
                  {copied === variant.key ? 'Copied' : 'Copy'}
                </button>
              </div>
            ))}
            <div className="inspector-actions">
              <button type="button" className="btn" onClick={() => window.open(active.url, '_blank')}>
                Open
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={loading}
                onClick={() => {
                  onDeleteImage(active.filename);
                  setActive(null);
                }}
              >
                Delete
              </button>
              <button type="button" className="btn" onClick={() => setActive(null)}>
                Close
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

export default ImageGallery;
